import * as React from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { CurrencyInput } from '@/components/ui/currency-input'
import { Label } from '@/components/ui/label'
import { DatePicker } from '@/components/shared/date-picker'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { formatCurrency, dollarsToCents, centsToDollars } from '@/lib/currency-utils'
import { Plus, Trash2, CheckCircle2, AlertCircle, LockKeyhole } from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import type { InvoicePaymentScheduleFormValue } from '@/lib/payment-schedule-utils'

interface InvoicePaymentScheduleEditorProps {
  schedules: InvoicePaymentScheduleFormValue[]
  invoiceTotalCents: number
  onSchedulesChange: (schedules: InvoicePaymentScheduleFormValue[]) => void
  disabled?: boolean
  lockedScheduleReasons?: Record<string, string>
  onApplyBalanceToLastPayment?: () => void
  onAddBalanceAsNewPayment?: () => void
}

/**
 * InvoicePaymentScheduleEditor - Editor for managing payment schedules on invoices
 *
 * Features:
 * - Quick templates (Full Payment, 50/50 Deposit, Deposit + Remainder, Custom)
 * - Fixed-amount schedules only (no percentage support like quotes)
 * - Add/remove/edit schedules
 * - Real-time validation with warnings (doesn't block submission)
 */
export function InvoicePaymentScheduleEditor({
  schedules,
  invoiceTotalCents,
  onSchedulesChange,
  disabled = false,
  lockedScheduleReasons = {},
  onApplyBalanceToLastPayment,
  onAddBalanceAsNewPayment,
}: InvoicePaymentScheduleEditorProps) {
  // Track selected template to allow re-selection
  const [selectedTemplate, setSelectedTemplate] = React.useState<string>('')
  const hasLockedSchedules = Object.keys(lockedScheduleReasons).length > 0
  const templatesDisabled = disabled || hasLockedSchedules

  // Apply quick templates
  const applyTemplate = (template: string) => {
    if (templatesDisabled) return

    const today = new Date()
    const in30Days = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000)

    let newSchedules: InvoicePaymentScheduleFormValue[] = []

    switch (template) {
      case 'full-upfront':
        newSchedules = [
          {
            sequenceNumber: 1,
            description: 'Full Payment',
            dueDate: today,
            amountCents: invoiceTotalCents,
          }
        ]
        break
      case '50-50': {
        const halfAmount = Math.round(invoiceTotalCents / 2)
        newSchedules = [
          {
            sequenceNumber: 1,
            description: 'Deposit (50%)',
            dueDate: today,
            amountCents: halfAmount,
          },
          {
            sequenceNumber: 2,
            description: 'Final Payment (50%)',
            dueDate: in30Days,
            amountCents: invoiceTotalCents - halfAmount, // Use remainder to ensure exact total
          }
        ]
        break
      }
      case 'deposit-remainder': {
        const depositAmount = Math.min(50000, invoiceTotalCents) // $500.00, capped at invoice total
        newSchedules = [
          {
            sequenceNumber: 1,
            description: 'Deposit',
            dueDate: today,
            amountCents: depositAmount,
          },
          {
            sequenceNumber: 2,
            description: 'Remainder',
            dueDate: in30Days,
            amountCents: Math.max(0, invoiceTotalCents - depositAmount),
          }
        ]
        break
      }
      case 'custom':
        // Clear schedules for custom
        newSchedules = []
        break
    }

    // Update schedules immediately - this triggers parent re-render
    onSchedulesChange(newSchedules)

    // Set the template value first to show selection, then reset to allow re-selection
    setSelectedTemplate(template)

    // Reset select after a brief delay to allow re-selection of the same template
    setTimeout(() => {
      setSelectedTemplate('')
    }, 100)

    if (template !== 'custom') {
      toast.success('Template applied')
    }
  }

  // Add new schedule
  const handleAddSchedule = () => {
    if (disabled) return

    const newSequence = schedules.length + 1
    const newSchedule: InvoicePaymentScheduleFormValue = {
      sequenceNumber: newSequence,
      description: `Payment ${newSequence}`,
      dueDate: new Date(),
      amountCents: 0,
    }
    onSchedulesChange([...schedules, newSchedule])
  }

  // Delete schedule
  const handleDeleteSchedule = (index: number) => {
    if (disabled) return

    const updated = schedules.filter((_, i) => i !== index)
    // Renumber sequence numbers
    const renumbered = updated.map((s, i) => ({
      ...s,
      sequenceNumber: i + 1,
    }))
    onSchedulesChange(renumbered)
  }

  // Update schedule
  const handleUpdateSchedule = (index: number, updates: Partial<InvoicePaymentScheduleFormValue>) => {
    if (disabled) return

    const updated = schedules.map((s, i) =>
      i === index ? { ...s, ...updates } : s
    )
    onSchedulesChange(updated)
  }

  // Validation logic (warning only, doesn't block submission)
  const validationResult = React.useMemo(() => {
    if (schedules.length === 0) {
      return {
        type: 'info' as const,
        message: 'No payment schedules configured. Invoice will be due in full.',
      }
    }

    const total = schedules.reduce((sum, s) => sum + (s.amountCents || 0), 0)

    if (total === invoiceTotalCents) {
      return {
        type: 'success' as const,
        message: `Total: $${formatCurrency(total)} matches invoice total`,
      }
    }

    if (total < invoiceTotalCents) {
      const remaining = invoiceTotalCents - total
      return {
        type: 'warning' as const,
        message: `Total: $${formatCurrency(total)} is $${formatCurrency(remaining)} less than invoice total of $${formatCurrency(invoiceTotalCents)}`,
      }
    }

    const excess = total - invoiceTotalCents
    return {
      type: 'warning' as const,
      message: `Total: $${formatCurrency(total)} exceeds invoice total by $${formatCurrency(excess)}`,
    }
  }, [schedules, invoiceTotalCents])

  const scheduleTotal = schedules.reduce((sum, schedule) => sum + (schedule.amountCents || 0), 0)
  const balanceDelta = invoiceTotalCents - scheduleTotal
  const showPositiveBalanceActions = balanceDelta > 0 && !disabled &&
    (onApplyBalanceToLastPayment || onAddBalanceAsNewPayment)
  const showNegativeBalanceAction = balanceDelta < 0 && !disabled && Boolean(onApplyBalanceToLastPayment)

  return (
    <div className="space-y-4">
      {/* Quick Templates */}
      <div className="space-y-2">
        <Label>Quick Templates</Label>
        <Select value={selectedTemplate} onValueChange={applyTemplate} disabled={templatesDisabled}>
          <SelectTrigger>
            <SelectValue placeholder="Select a template..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="full-upfront">Full Payment Upfront</SelectItem>
            <SelectItem value="50-50">50/50 Deposit</SelectItem>
            <SelectItem value="deposit-remainder">Deposit + Remainder</SelectItem>
            <SelectItem value="custom">Custom (start from scratch)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Info message */}
      <p className="text-xs text-muted-foreground">
        Create payment schedules to break the invoice into multiple installments. Each schedule represents a payment due on a specific date.
      </p>

      {hasLockedSchedules && !disabled && (
        <div role="alert" className="flex items-start gap-2 rounded-lg border border-status-warning-border bg-status-warning-bg p-3 text-sm text-status-warning-fg">
          <LockKeyhole className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          <p className="min-w-0">
            Locked payment items cannot be edited. Other schedule items can still be updated.
          </p>
        </div>
      )}

      {showPositiveBalanceActions && (
        <div role="alert" className="space-y-3 rounded-lg border border-status-warning-border bg-status-warning-bg p-4 text-sm text-status-warning-fg">
          <div className="flex items-start gap-2">
            <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
            <div className="min-w-0">
              <p className="font-medium">Apply ${formatCurrency(balanceDelta)} to the payment schedule</p>
              <p className="mt-0.5 text-status-warning-fg">
                Choose how the added invoice amount should be collected.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              onClick={onApplyBalanceToLastPayment}
              disabled={!onApplyBalanceToLastPayment}
            >
              Add to Last Payment Item
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={onAddBalanceAsNewPayment}
              disabled={!onAddBalanceAsNewPayment}
            >
              Add New Schedule Item
            </Button>
          </div>
        </div>
      )}

      {showNegativeBalanceAction && (
        <div role="alert" className="space-y-3 rounded-lg border border-status-warning-border bg-status-warning-bg p-4 text-sm text-status-warning-fg">
          <div className="flex items-start gap-2">
            <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
            <div className="min-w-0">
              <p className="font-medium">Reduce the payment schedule by ${formatCurrency(Math.abs(balanceDelta))}</p>
              <p className="mt-0.5 text-status-warning-fg">
                Apply the lower invoice total to the editable payment items.
              </p>
            </div>
          </div>
          <Button
            type="button"
            size="sm"
            onClick={onApplyBalanceToLastPayment}
          >
            Apply Reduction
          </Button>
        </div>
      )}

      {/* Empty state */}
      {schedules.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-xl border bg-muted/30 py-12 text-center">
          <p className="mb-4 text-sm text-muted-foreground">
            No payment schedules configured. Click below to add your first schedule.
          </p>
          <Button type="button" onClick={handleAddSchedule} disabled={disabled}>
            <Plus className="size-4" aria-hidden="true" />
            Add Payment Schedule
          </Button>
        </div>
      )}

      {/* Schedule list */}
      {schedules.length > 0 && (
        <div className="space-y-3">
          {schedules.map((schedule, index) => {
            const dueDate = schedule.dueDate
            const lockReason = schedule.id ? lockedScheduleReasons[schedule.id] : undefined
            const scheduleDisabled = disabled || Boolean(lockReason)

            return (
              <div
                key={`${schedule.sequenceNumber}-${schedule.description}-${index}`}
                className={cn('rounded-xl border px-4 py-3', scheduleDisabled && 'bg-muted/40')}
              >
                <div className="space-y-3">
                  {/* Header with sequence number and delete button */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="flex size-6 items-center justify-center rounded-full bg-primary/10 text-xs font-medium tabular-nums text-primary">
                        {schedule.sequenceNumber}
                      </div>
                      <span className="text-sm font-medium">Payment {schedule.sequenceNumber}</span>
                      {lockReason && (
                        <span className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                          <LockKeyhole className="size-3" aria-hidden="true" />
                          Locked
                        </span>
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteSchedule(index)}
                      disabled={scheduleDisabled}
                      aria-label={`Delete payment ${schedule.sequenceNumber}`}
                    >
                      <Trash2 className="size-4 text-destructive" aria-hidden="true" />
                    </Button>
                  </div>

                  {lockReason && (
                    <p className="text-xs text-muted-foreground">{lockReason}</p>
                  )}

                  {/* Description */}
                  <div className="space-y-2">
                    <Label htmlFor={`description-${index}`}>Description</Label>
                    <Input
                      id={`description-${index}`}
                      value={schedule.description}
                      onChange={(e) => handleUpdateSchedule(index, { description: e.target.value })}
                      placeholder="e.g., Deposit, Final Payment"
                      disabled={scheduleDisabled}
                    />
                  </div>

                  {/* Due date and amount - side by side */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor={`due-date-${index}`}>Due Date</Label>
                      <DatePicker
                        value={dueDate}
                        onChange={(date) => {
                          if (date) {
                            handleUpdateSchedule(index, { dueDate: date })
                          }
                        }}
                        disabled={scheduleDisabled}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor={`amount-${index}`}>Amount</Label>
                      <CurrencyInput
                        id={`amount-${index}`}
                        value={schedule.amountCents ? centsToDollars(schedule.amountCents) : undefined}
                        onChange={(value) => {
                          handleUpdateSchedule(index, { amountCents: dollarsToCents(value) })
                        }}
                        disabled={scheduleDisabled}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )
          })}

          {/* Add schedule button */}
          <Button
            type="button"
            variant="outline"
            onClick={handleAddSchedule}
            disabled={disabled}
            className="w-full"
          >
            <Plus className="size-4" aria-hidden="true" />
            Add Another Schedule
          </Button>
        </div>
      )}

      {/* Validation display */}
      <div
        role={validationResult.type === 'warning' ? 'alert' : 'status'}
        aria-live="polite"
        className={cn(
          'flex items-start gap-2 rounded-lg border p-3 text-sm',
          validationResult.type === 'success' && 'border-status-success-border bg-status-success-bg text-status-success-fg',
          validationResult.type === 'warning' && 'border-status-warning-border bg-status-warning-bg text-status-warning-fg',
          validationResult.type === 'info' && 'border-status-info-border bg-status-info-bg text-status-info-fg',
        )}
      >
        {validationResult.type === 'success'
          ? <CheckCircle2 className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          : <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />}
        <p className="min-w-0">{validationResult.message}</p>
      </div>
    </div>
  )
}
